import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/Button";
import { Icon } from "@/components/Icon";
import { Logo } from "@/components/Logo";
import { NavigationCommand } from "@/components/workspace/NavigationCommand";
import { WorkspaceSidebar } from "@/components/workspace/WorkspaceSidebar";
import { TopNavigation } from "@/components/workspace/TopNavigation";
import { NavigationExplorer } from "@/components/workspace/NavigationExplorer";
import { useNavigationExplorer } from "@/components/workspace/useNavigationExplorer";
import { useNavigationMode } from "@/components/workspace/useNavigationMode";
import {
    buildNavigationAreas,
    matchesNavigation,
    type NavigationItem,
} from "@/components/workspace/navigation";
import {
    applyBrandTheme,
    DEFAULT_BRAND_COLOR,
} from "@/components/workspace/branding";

interface AppShellProps {
    children: React.ReactNode;
    items: NavigationItem[];
    favorites: string[];
    onFavorite: (id: string) => void;
    onFavoritesChange: (ids: string[]) => void;
    companyName: string;
    companyLogo?: string | null;
    canSwitchCompany: boolean;
    onCompany: () => void;
    brandColor?: string | null;
    user: { id: number; name: string; email: string; avatar?: string | null };
    scope: string;
    unreadCount: number;
    onNotifications: () => void;
}

const readRecent = (key: string): string[] => {
    try {
        const value: unknown = JSON.parse(sessionStorage.getItem(key) || "[]");
        return Array.isArray(value)
            ? value
                  .filter((id): id is string => typeof id === "string")
                  .slice(0, 4)
            : [];
    } catch {
        return [];
    }
};

export const AppShell = ({
    children,
    items,
    favorites,
    onFavorite,
    onFavoritesChange,
    companyName,
    companyLogo,
    canSwitchCompany,
    onCompany,
    brandColor,
    user,
    scope,
    unreadCount,
    onNotifications,
}: AppShellProps) => {
    const { t } = useTranslation("global");
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const areas = buildNavigationAreas(
        items,
        t("workspace.purchases"),
        t("workspace.contracting")
    );
    const allItems = areas.flatMap((area) => area.items);
    const activeItem = allItems.find((item) =>
        matchesNavigation(item, pathname)
    );
    const activeArea = areas.find((area) =>
        area.items.some((item) => item.id === activeItem?.id)
    );
    const [areaId, setAreaId] = useState(areas[0]?.id);
    const area =
        activeArea || areas.find((entry) => entry.id === areaId) || areas[0];
    const [collapsed, setCollapsed] = useState(
        () => localStorage.getItem("vigepra_sidebar_collapsed") === "true"
    );
    const [navigationMode] = useNavigationMode();
    const explorer = useNavigationExplorer();
    const [commandOpen, setCommandOpen] = useState(false);
    const recentKey = `vigepra_recent_screens:${scope}`;
    const [recentIds, setRecentIds] = useState(() => readRecent(recentKey));
    useEffect(() => {
        applyBrandTheme(brandColor);
        return () => applyBrandTheme(DEFAULT_BRAND_COLOR);
    }, [brandColor]);
    useEffect(() => {
        const id = activeItem?.id;
        const next = id
            ? [
                  id,
                  ...readRecent(recentKey).filter(
                      (previous) => previous !== id
                  ),
              ].slice(0, 4)
            : readRecent(recentKey);
        setRecentIds(next);
        try {
            sessionStorage.setItem(recentKey, JSON.stringify(next));
        } catch {
            /* Storage is optional. */
        }
    }, [activeItem?.id, recentKey]);
    useEffect(() => {
        const shortcut = (event: KeyboardEvent): void => {
            if (
                (event.ctrlKey || event.metaKey) &&
                event.key.toLowerCase() === "k"
            ) {
                event.preventDefault();
                setCommandOpen((previous) => !previous);
            }
        };
        window.addEventListener("keydown", shortcut);
        return () => window.removeEventListener("keydown", shortcut);
    }, []);
    const changeArea = (id: string): void => {
        setAreaId(id);
        const next = areas.find((entry) => entry.id === id)?.items[0];
        if (next?.path) {
            navigate(next.path);
        }
    };
    const toggleCollapsed = (): void => {
        setCollapsed((previous) => {
            try {
                localStorage.setItem(
                    "vigepra_sidebar_collapsed",
                    String(!previous)
                );
            } catch {
                /* Storage is optional. */
            }
            return !previous;
        });
    };
    const reorderFavorite = (sourceId: string, targetId: string): void => {
        const next = [...favorites];
        const sourceIndex = next.indexOf(sourceId);
        const targetIndex = next.indexOf(targetId);
        if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
            return;
        }
        next.splice(sourceIndex, 1);
        next.splice(targetIndex, 0, sourceId);
        onFavoritesChange(next);
    };
    const recent = recentIds
        .filter((id) => id !== activeItem?.id)
        .map((id) => allItems.find((item) => item.id === id))
        .filter((item): item is NavigationItem => Boolean(item));
    const sidebarProps = {
        areas,
        area,
        favorites,
        recent,
        onArea: changeArea,
        onFavorite,
        onReorderFavorite: reorderFavorite,
    };
    const initials = user.name
        ?.trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part[0])
        .join("");
    const currentLabel = explorer.open
        ? t("workspace.all_areas")
        : activeItem?.label ||
          t(
              pathname.startsWith("/settings")
                  ? "settings.title"
                  : "workspace.home"
          );

    return (
        <div className="min-h-screen bg-slate-50 text-slate-800 dark:bg-dark-bg dark:text-zinc-100">
            <a
                href="#workspace-content"
                className="sr-only fixed left-4 top-2 z-50 rounded-lg bg-box p-3 focus:not-sr-only"
            >
                {t("workspace.skip_content")}
            </a>
            <header className="sticky top-0 z-30 flex h-14 items-center border-b border-slate-200 bg-white dark:border-white/10 dark:bg-dark-box">
                <div
                    className={`hidden h-full shrink-0 items-center justify-between border-r border-slate-200 px-3 dark:border-white/10 lg:flex ${navigationMode === "sidebar" ? (collapsed ? "w-20" : "w-64") : "border-r-0"}`}
                >
                    {(navigationMode === "topbar" || !collapsed) && (
                        <Link
                            to="/"
                            aria-label={`${t("workspace.brand")} · ${t("workspace.home")}`}
                            className="flex min-w-0 items-center"
                        >
                            <Logo className="h-7 w-24" />
                        </Link>
                    )}
                    {navigationMode === "sidebar" && (
                        <Button
                            variant="ghost"
                            icon={collapsed ? "chevronRight" : "chevronLeft"}
                            onlyicon
                            size="sm"
                            onClick={toggleCollapsed}
                            aria-label={t(
                                collapsed
                                    ? "workspace.expand"
                                    : "workspace.collapse"
                            )}
                            title={t(
                                collapsed
                                    ? "workspace.expand"
                                    : "workspace.collapse"
                            )}
                        />
                    )}
                </div>
                <div className="flex min-w-0 flex-1 items-center gap-0 px-2 sm:gap-1 sm:px-3">
                    <div className="mr-0 min-w-0 flex-1 pr-1 sm:pr-1 lg:flex-none">
                        <Button
                            variant="custom"
                            icon={
                                companyLogo ? (
                                    <img
                                        src={companyLogo}
                                        alt=""
                                        className="h-5 w-5 rounded-full object-cover"
                                    />
                                ) : (
                                    <Icon
                                        value="building2"
                                        size={18}
                                        className="text-primary"
                                    />
                                )
                            }
                            endIcon={
                                canSwitchCompany ? "chevronDown" : undefined
                            }
                            onClick={onCompany}
                            disabled={!canSwitchCompany}
                            className="!h-9 w-full min-w-0 !justify-start !gap-2 !rounded-lg !border !border-slate-200 !bg-slate-100/80 !px-2.5 !text-slate-800 hover:!bg-slate-200/70 disabled:!opacity-100 dark:!border-white/15 dark:!bg-white/5 dark:!text-zinc-100 dark:hover:!bg-white/10"
                            contentClassName="min-w-0"
                            title={companyName}
                        >
                            <span className="truncate text-xs font-semibold lg:max-w-32 xl:max-w-44 2xl:max-w-56">
                                {companyName}
                            </span>
                        </Button>
                    </div>
                    <TopNavigation
                        areas={areas}
                        activeAreaId={activeArea?.id}
                        favorites={favorites}
                        onFavorite={onFavorite}
                        onReorderFavorite={reorderFavorite}
                        showAreas={navigationMode === "topbar"}
                        exploring={explorer.open}
                    />
                    <div className="ml-auto flex shrink-0 items-center gap-0.5 sm:gap-1">
                        <Button
                            variant="ghost"
                            onClick={() => setCommandOpen(true)}
                            icon="search"
                            aria-label={t("workspace.search_title")}
                            className="!px-1.5 !text-slate-600 dark:!text-zinc-300"
                        >
                            <span className="hidden text-xs xl:inline">
                                {t("workspace.search_title")}
                            </span>
                            <kbd className="hidden rounded border border-slate-200 px-1.5 py-0.5 text-[10px] xl:inline dark:border-white/15">
                                Ctrl K
                            </kbd>
                        </Button>
                        <div className="relative">
                            <Button
                                variant="ghost"
                                icon="bell"
                                onlyicon
                                onClick={onNotifications}
                                title={t("notifications.modal_title")}
                                aria-label={t("notifications.modal_title")}
                            />
                            {unreadCount > 0 && (
                                <span className="pointer-events-none absolute -right-1 -top-1 rounded-full bg-primary px-1.5 text-[10px] text-white">
                                    {unreadCount > 99 ? "99+" : unreadCount}
                                </span>
                            )}
                        </div>
                        <Link
                            to="/settings/account"
                            title={user.email}
                            className="ml-1 flex items-center gap-2 border-l border-slate-200 pl-2 !text-inherit dark:border-white/10"
                        >
                            <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-xs font-bold text-primary">
                                {user.avatar ? (
                                    <img
                                        src={user.avatar}
                                        alt=""
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    initials
                                )}
                            </span>
                            <span className="hidden max-w-36 truncate text-xs font-medium 2xl:block">
                                {user.name}
                            </span>
                        </Link>
                    </div>
                </div>
            </header>
            {navigationMode === "sidebar" && (
                <aside
                    className={`fixed bottom-0 left-0 top-14 hidden border-r border-slate-200 bg-white dark:border-white/10 dark:bg-dark-box lg:block ${collapsed ? "w-20" : "w-64"}`}
                >
                    <WorkspaceSidebar {...sidebarProps} collapsed={collapsed} />
                </aside>
            )}
            <div
                className={`min-w-0 ${navigationMode === "sidebar" ? (collapsed ? "lg:pl-20" : "lg:pl-64") : ""}`}
            >
                <div
                    className="mb-1 flex items-center gap-2 px-4 pt-2 text-xs text-slate-500 sm:px-5 dark:text-zinc-400"
                    aria-label={t("workspace.location")}
                >
                    <Link
                        to="/"
                        aria-label={t("workspace.home")}
                        className="!text-inherit hover:text-primary"
                    >
                        <Icon value="house" size={14} />
                    </Link>
                    <Icon value="chevronRight" size={12} />
                    {!explorer.open &&
                        activeArea &&
                        activeArea.label !== currentLabel && (
                            <>
                                <span>{activeArea.label}</span>
                                <Icon value="chevronRight" size={12} />
                            </>
                        )}
                    <span
                        aria-current="page"
                        className="font-medium text-slate-700 dark:text-zinc-200"
                    >
                        {currentLabel}
                    </span>
                </div>
                <main
                    id="workspace-content"
                    tabIndex={-1}
                    className="relative mx-auto w-full min-w-0 !max-w-none !p-4 outline-none sm:!px-5 sm:!py-4"
                >
                    {explorer.open ? (
                        <NavigationExplorer
                            areas={areas}
                            favorites={favorites}
                            onFavorite={onFavorite}
                            onClose={explorer.close}
                        />
                    ) : (
                        children
                    )}
                </main>
            </div>
            <NavigationCommand
                areas={areas}
                open={commandOpen}
                onClose={() => setCommandOpen(false)}
            />
        </div>
    );
};
